import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../API';
import { useAuth } from '../../providers/AuthContext';
import {
    QuestionAnswerControls,
    FREE_TEXT_CATEGORY_ID,
    NONE_CATEGORY_ID,
    DEFAULT_NONE_CATEGORY_LABEL,
    getQuestionDisplayName,
    getFreeTextOptionLabelForDisplay,
    getAnswerCategoryIds,
    getAnswerCategoryLabels,
    isQuestionAnswerComplete,
    toggleMultiCategorySelection,
    getSurveyDisplayTitle,
    getSurveyTypeLabel,
    isSurveyResponseComplete,
    buildSurveyAnswersPayload,
    migrateSurveyUiAnswers,
    SURVEY_TYPES,
} from './questions';
import styles from './ProjectSurveyPage.module.css';

function emptyUiAnswer() {
    return {
        categoryId: '',
        categoryLabel: '',
        categoryIds: [],
        categoryLabels: [],
        freeText: '',
    };
}

function ProjectSurveyPage() {
    const { folderId, surveyType } = useParams();
    const navigate = useNavigate();
    const baseId = useId();
    const { user: currentUser } = useAuth();

    const [projectName, setProjectName] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [survey, setSurvey] = useState(null);
    const [answers, setAnswers] = useState({});
    const [status, setStatus] = useState('not_started');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveMessage, setSaveMessage] = useState(null);
    const [clearConfirm, setClearConfirm] = useState(false);
    const [clearing, setClearing] = useState(false);
    const answersRef = useRef(answers);
    answersRef.current = answers;

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        setSaveError(null);
        setSaveMessage(null);
        setClearConfirm(false);

        Promise.all([
            api.get(`projects/${folderId}`, {}),
            api.get(`projects/${folderId}/surveys/${surveyType}/response`, {}),
        ])
            .then(([projectRes, responseRes]) => {
                if (cancelled) return;
                if (projectRes.status === 200) {
                    setProjectName(projectRes.data?.name || '');
                    const participants = projectRes.data?.girderProjects?.participants || [];
                    const myId = currentUser ? String(currentUser._id) : null;
                    const myEntry = participants.find((p) => String(p.userId) === myId);
                    const roles = myEntry?.roles || [];
                    setIsAdmin(Boolean(currentUser?.admin) || roles.includes('admin'));
                }
                if (responseRes.status !== 200) {
                    setError(
                        responseRes.data?.message
                        || 'Unable to load this survey.',
                    );
                    setSurvey(null);
                    return;
                }
                const s = responseRes.data?.survey;
                const resp = responseRes.data?.response || {};
                setSurvey(s);
                setStatus(resp.status || 'not_started');
                setAnswers(migrateSurveyUiAnswers(s, resp.answers));
            })
            .catch((e) => {
                if (!cancelled) setError(e.message || 'Failed to load survey.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [folderId, surveyType, currentUser]);

    const getAnswer = useCallback(
        (questionId) => answers[questionId] ?? emptyUiAnswer(),
        [answers],
    );

    const setClassification = useCallback((questionId, nextId, nextLabel) => {
        setAnswers((prev) => {
            const prevA = prev[questionId] ?? emptyUiAnswer();
            const next = {
                ...prev,
                [questionId]: {
                    categoryId: nextId,
                    categoryLabel: nextLabel ?? '',
                    categoryIds: [],
                    categoryLabels: [],
                    freeText: String(nextId) === FREE_TEXT_CATEGORY_ID
                        ? (prevA.freeText ?? '')
                        : '',
                },
            };
            answersRef.current = next;
            return next;
        });
        setSaveMessage(null);
    }, []);

    const toggleMultiClassification = useCallback((questionId, toggleId, toggleLabel) => {
        const q = survey?.questions?.find((x) => x.id === questionId);
        const ftLabel = getFreeTextOptionLabelForDisplay(q?.freeText?.optionLabel);
        const id = String(toggleId ?? '');
        setAnswers((prev) => {
            const prevA = prev[questionId] ?? emptyUiAnswer();
            const currentIds = getAnswerCategoryIds(prevA);
            const currentLabels = getAnswerCategoryLabels(prevA, currentIds);
            let label = toggleLabel;
            if (id === NONE_CATEGORY_ID) {
                label = toggleLabel || DEFAULT_NONE_CATEGORY_LABEL;
            } else if (id === FREE_TEXT_CATEGORY_ID) {
                label = toggleLabel || ftLabel;
            }
            const { categoryIds, categoryLabels } = toggleMultiCategorySelection(
                currentIds,
                currentLabels,
                id,
                label,
            );
            let freeText = prevA.freeText ?? '';
            if (categoryIds.includes(NONE_CATEGORY_ID)) {
                freeText = '';
            } else if (!categoryIds.includes(FREE_TEXT_CATEGORY_ID)) {
                freeText = '';
            }
            const next = {
                ...prev,
                [questionId]: {
                    ...prevA,
                    categoryIds,
                    categoryLabels,
                    categoryId: categoryIds[0] ?? '',
                    categoryLabel: categoryLabels[0] ?? '',
                    freeText,
                },
            };
            answersRef.current = next;
            return next;
        });
        setSaveMessage(null);
    }, [survey]);

    const setFreeTextContent = useCallback((questionId, text) => {
        setAnswers((prev) => {
            const prevA = prev[questionId] ?? emptyUiAnswer();
            const next = {
                ...prev,
                [questionId]: { ...prevA, freeText: text },
            };
            answersRef.current = next;
            return next;
        });
        setSaveMessage(null);
    }, []);

    async function persist(nextStatus) {
        if (!survey) return;
        setSaving(true);
        setSaveError(null);
        setSaveMessage(null);
        try {
            const payload = {
                answers: buildSurveyAnswersPayload(survey, answersRef.current),
                status: nextStatus,
            };
            const r = await api.put(
                `projects/${folderId}/surveys/${surveyType}/response`,
                { json: payload },
            );
            if (r.status !== 200) {
                setSaveError(r.data?.message || 'Save failed.');
                return;
            }
            setStatus(r.data?.status || nextStatus);
            setSaveMessage(
                nextStatus === 'completed'
                    ? 'Survey submitted.'
                    : 'Progress saved.',
            );
            if (nextStatus === 'completed' && surveyType === SURVEY_TYPES.PRE_TEST) {
                setTimeout(() => {
                    navigate(`/projects/${folderId}/participate`);
                }, 600);
            }
        } catch (e) {
            setSaveError(e.message || 'Save failed.');
        } finally {
            setSaving(false);
        }
    }

    async function clearMyResponse() {
        if (!clearConfirm) {
            setClearConfirm(true);
            return;
        }
        setClearConfirm(false);
        setClearing(true);
        setSaveError(null);
        setSaveMessage(null);
        try {
            const r = await api.delete(
                `projects/${folderId}/surveys/${surveyType}/response`,
                {},
            );
            if (r.status !== 200 && r.status !== 404) {
                setSaveError(r.data?.message || 'Could not clear survey response.');
                return;
            }
            const empty = migrateSurveyUiAnswers(survey, {});
            answersRef.current = empty;
            setAnswers(empty);
            setStatus('not_started');
            setSaveMessage('Your response was cleared.');
        } catch (e) {
            setSaveError(e.message || 'Could not clear survey response.');
        } finally {
            setClearing(false);
        }
    }

    function applySelectValue(questionId, v) {
        const q = survey?.questions?.find((x) => x.id === questionId);
        if (!q) return;
        const ftLabel = getFreeTextOptionLabelForDisplay(q.freeText?.optionLabel);
        if (!v) {
            setClassification(questionId, '', '');
            return;
        }
        if (v === FREE_TEXT_CATEGORY_ID) {
            setClassification(questionId, FREE_TEXT_CATEGORY_ID, ftLabel);
            return;
        }
        const cat = q.categories.find((c) => String(c.id) === v);
        setClassification(questionId, v, cat?.label ?? cat?.id ?? '');
    }

    if (loading) {
        return <div className={styles.loading}>Loading survey…</div>;
    }

    if (error) {
        return (
            <div className={styles.page}>
                <div className={styles.errorMessage}>{error}</div>
                <Link
                    to={`/projects/${folderId}/participate`}
                    className={styles.backLink}
                >
                    ← Back to project
                </Link>
            </div>
        );
    }

    if (!survey) {
        return (
            <div className={styles.page}>
                <p>Survey not found.</p>
                <Link
                    to={`/projects/${folderId}/participate`}
                    className={styles.backLink}
                >
                    ← Back to project
                </Link>
            </div>
        );
    }

    const questions = survey.questions || [];
    const displayTitle = getSurveyDisplayTitle(survey);
    const typeLabel = getSurveyTypeLabel(survey.type);
    const complete = isSurveyResponseComplete(survey, answers);
    const alreadyDone = status === 'completed';
    const readOnly = alreadyDone;
    const hasStoredResponse = status !== 'not_started'
        || questions.some((q) => isQuestionAnswerComplete(q, getAnswer(q.id)));
    const showAdminClear = isAdmin && hasStoredResponse;

    return (
        <div className={styles.page}>
            <header className={styles.pageHeader}>
                <Link
                    to={`/projects/${folderId}/participate`}
                    className={styles.backLink}
                >
                    ← Back to project
                </Link>
                <h1 className={styles.projectName}>{projectName}</h1>
                <p className={styles.surveyTypeMeta}>
                    <span className={styles.typeBadge}>{typeLabel}</span>
                    {displayTitle !== typeLabel ? (
                        <span className={styles.surveyTitle}>{displayTitle}</span>
                    ) : null}
                </p>
            </header>

            {survey.instructions ? (
                <div className={styles.instructions}>
                    <p>{survey.instructions}</p>
                </div>
            ) : null}

            {alreadyDone ? (
                <p className={styles.completedBanner} role="status">
                    You have completed this survey.
                </p>
            ) : null}

            <div className={styles.questionList}>
                {questions.map((q, i) => {
                    const labelId = `${baseId}-q-${q.id}-label`;
                    const a = getAnswer(q.id);
                    const answered = isQuestionAnswerComplete(q, a);
                    const title = getQuestionDisplayName(q, i);
                    return (
                        <section
                            key={q.id}
                            className={[
                                styles.questionCard,
                                answered ? styles.questionCardAnswered : '',
                            ].filter(Boolean).join(' ')}
                            aria-labelledby={labelId}
                        >
                            <header className={styles.questionHeader}>
                                <span className={styles.indexBadge}>
                                    {i + 1}
                                    /
                                    {questions.length}
                                </span>
                                <h2 id={labelId} className={styles.questionTitle}>
                                    {title}
                                </h2>
                                {answered ? (
                                    <span className={styles.checkMark} aria-hidden>✓</span>
                                ) : null}
                            </header>
                            <QuestionAnswerControls
                                question={q}
                                categoryId={a.categoryId}
                                categoryIds={getAnswerCategoryIds(a)}
                                freeTextAnswer={a.freeText}
                                disabled={readOnly || saving}
                                labelledById={labelId}
                                freeTextPresentation="inline"
                                onCommitSelection={(v) => applySelectValue(q.id, v)}
                                onToggleMultiCategory={(id, label) => {
                                    toggleMultiClassification(q.id, id, label);
                                }}
                                onFreeTextChange={(text) => setFreeTextContent(q.id, text)}
                            />
                        </section>
                    );
                })}
            </div>

            {!readOnly ? (
                <div className={styles.actions}>
                    {saveError ? (
                        <p className={styles.saveError} role="alert">{saveError}</p>
                    ) : null}
                    {saveMessage ? (
                        <p className={styles.saveMessage} role="status">{saveMessage}</p>
                    ) : null}
                    {showAdminClear ? (
                        <button
                            type="button"
                            className={styles.buttonDanger}
                            disabled={saving || clearing}
                            onClick={() => clearMyResponse()}
                        >
                            {clearing
                                ? 'Clearing…'
                                : clearConfirm
                                    ? 'Click again to clear'
                                    : 'Clear my response'}
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className={styles.buttonSecondary}
                        disabled={saving || clearing}
                        onClick={() => persist('in_progress')}
                    >
                        Save progress
                    </button>
                    <button
                        type="button"
                        className={styles.buttonPrimary}
                        disabled={saving || clearing || !complete}
                        title={
                            complete
                                ? 'Submit completed survey'
                                : 'Answer all questions to submit'
                        }
                        onClick={() => persist('completed')}
                    >
                        {saving ? 'Saving…' : 'Submit'}
                    </button>
                </div>
            ) : (
                <div className={styles.actions}>
                    {saveError ? (
                        <p className={styles.saveError} role="alert">{saveError}</p>
                    ) : null}
                    {saveMessage ? (
                        <p className={styles.saveMessage} role="status">{saveMessage}</p>
                    ) : null}
                    {showAdminClear ? (
                        <button
                            type="button"
                            className={styles.buttonDanger}
                            disabled={clearing}
                            onClick={() => clearMyResponse()}
                        >
                            {clearing
                                ? 'Clearing…'
                                : clearConfirm
                                    ? 'Click again to clear'
                                    : 'Clear my response'}
                        </button>
                    ) : null}
                    <Link
                        to={`/projects/${folderId}/participate`}
                        className={styles.buttonPrimaryLink}
                    >
                        Return to project
                    </Link>
                </div>
            )}
        </div>
    );
}

export default ProjectSurveyPage;
